// Settings · Title filter — chip-list editor for the CV-derived
// per-user title filter (auto-applied at scrape time).
//
// User flow:
//   - User uploads CV → server runs cv_extract_title_filter →
//     anchors + cv_summary populate, filter auto-activates
//   - User can land here from Settings or from the Scanner indicator
//   - Edit anchors (add/remove chips) — filter logic on next scrape
//   - Toggle the kill switch — filter immediately inactive
//   - "Re-extract" — re-fires the LLM job for current CV
//
// We deliberately don't expose `cv_summary` for editing — it's used
// as LLM context only, and a hand-edited summary would diverge from
// the CV in confusing ways. Read-only display so the user can sanity-
// check what the LLM understood about them.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Plus, RotateCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../lib/api';
import type { TitleFilterState } from '../lib/types';

export default function SettingsFilter() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [newAnchor, setNewAnchor] = useState('');

  const stateQ = useQuery({
    queryKey: ['title-filter'],
    queryFn: () => apiGet<TitleFilterState>('/api/title-filter'),
    staleTime: 10_000,
  });

  // Whether the user already has a CV on file. Determines if the
  // empty-state shows "Generate from your CV" (extract directly) or
  // "Upload a CV first" (no CV yet).
  const cvStatusQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<{ file: string | null; chars: number }>('/api/cv-status'),
    staleTime: 30_000,
  });

  const saveAnchors = useMutation({
    mutationFn: (anchors: string[]) =>
      apiPut<TitleFilterState>('/api/title-filter', { domainAnchors: anchors }),
    onSuccess: (data) => qc.setQueryData(['title-filter'], data),
  });

  // Seniority signal save (Phase 5). Uses the same /api/title-filter PUT
  // endpoint — server already validates seniorityLevel ∈ allowedLevels,
  // yearsExperience ∈ [0,60], readyForOvershoot ∈ {0,1,2}. Setting any
  // of these flips seniority_user_edited=true so the next CV re-extract
  // doesn't overwrite — that pin is intentional, gives the user the
  // last word over the LLM.
  const saveSeniority = useMutation({
    mutationFn: (patch: {
      seniorityLevel?: string | null;
      yearsExperience?: number | null;
      readyForOvershoot?: number | null;
    }) => apiPut<TitleFilterState>('/api/title-filter', patch),
    onSuccess: (data) => qc.setQueryData(['title-filter'], data),
  });

  const setDisabled = useMutation({
    mutationFn: (disabled: boolean) =>
      apiPut<TitleFilterState>('/api/title-filter', { userDisabled: disabled }),
    onSuccess: (data) => qc.setQueryData(['title-filter'], data),
  });

  const reExtract = useMutation({
    mutationFn: () => apiPost<{ job_id: string }>('/api/title-filter/extract'),
    onSuccess: () => {
      // Result lands via job queue; refetch in 30 s and again at 90 s
      // to catch most CVs (extract takes 5-15 s typical).
      setTimeout(() => qc.invalidateQueries({ queryKey: ['title-filter'] }), 30_000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['title-filter'] }), 90_000);
    },
  });

  const data = stateQ.data;
  const anchors = data?.domainAnchors ?? [];

  const removeAnchor = (idx: number) => {
    const next = anchors.filter((_, i) => i !== idx);
    saveAnchors.mutate(next);
  };

  const addAnchor = () => {
    const v = newAnchor.trim().toLowerCase();
    if (!v) return;
    if (anchors.includes(v)) {
      setNewAnchor('');
      return;
    }
    saveAnchors.mutate([...anchors, v]);
    setNewAnchor('');
  };

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/settings')}>
        ← Settings
      </button>
      <PageHeader
        eyebrow="Settings · automation"
        title="Title filter."
        sub="Auto-derived from your CV. Drops obviously irrelevant jobs before they reach the evaluation queue. The system never tells you what got dropped — just like a quiet inbox filter."
      />

      {/* Readiness banner — shown whenever the title filter isn't fully
          populated. Scans are blocked upstream (Scanner page disables
          its buttons + server returns 412); this banner explains why
          and points at the cards below that fix it. */}
      {data && !data.ready && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            padding: '14px 18px',
            marginBottom: 18,
            borderRadius: 8,
            border: '1px solid rgba(170,140,30,0.35)',
            background: 'rgba(170,140,30,0.08)',
            color: '#7a5e10',
          }}
        >
          <div style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>⚠</div>
          <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55 }}>
            <div style={{ fontWeight: 600, color: '#5a4408', marginBottom: 4 }}>
              Some fields are still missing — fill them in below to enable scanning.
            </div>
            <div>
              Missing:{' '}
              <strong>
                {data.missing.map((m) => ({
                  cv_summary: 'CV summary',
                  domain_anchors: 'anchor keywords (≥3)',
                  seniority_level: 'seniority level',
                  years_experience: 'years of experience',
                }[m] || m)).join(', ')}
              </strong>
              . Click <em>Re-extract</em> below to refresh from your CV, then
              fill anything that's still empty in the cards.
            </div>
          </div>
        </div>
      )}

      {!data ? (
        <div className="card" style={{ padding: 22 }}>
          <div className="empty">Loading…</div>
        </div>
      ) : !data.exists ? (
        (() => {
          const hasCv = !!(cvStatusQ.data && cvStatusQ.data.chars && cvStatusQ.data.chars > 100);
          return (
            <div className="card" style={{ padding: 22 }}>
              {hasCv ? (
                <>
                  <div className="empty">
                    <span className="serif">No filter yet.</span>
                    You've uploaded a CV — click below to extract anchor keywords + seniority from it.
                  </div>
                  <button
                    className="btn primary"
                    style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => reExtract.mutate()}
                    disabled={reExtract.isPending}
                  >
                    <RotateCw size={13} />
                    {reExtract.isPending ? 'Queued — checking back in 30s…' : 'Generate from my CV'}
                  </button>
                  {reExtract.isPending && (
                    <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink3)' }}>
                      Takes ~10–30s. This page will refresh automatically.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="empty">
                    <span className="serif">No filter yet.</span>
                    Upload a CV first — the system will auto-extract anchors from it.
                  </div>
                  <button className="btn" style={{ marginTop: 14 }} onClick={() => nav('/cv/upload')}>
                    Upload CV ↗
                  </button>
                </>
              )}
            </div>
          );
        })()
      ) : (
        <>
          {/* Status row */}
          <div
            className="card"
            style={{
              padding: '14px 18px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <div className="tab" style={{ marginBottom: 4 }}>Status</div>
              <div style={{ fontSize: 14, color: data.active ? 'var(--pri)' : 'var(--ink2)' }}>
                {data.active
                  ? `● Active — drops irrelevant titles before scrape writes pipeline.md`
                  : data.userDisabled
                    ? '○ Disabled by you — every scraped title flows through unfiltered'
                    : '○ Inactive — extraction failed health checks; re-run below'}
              </div>
              {data.extractedAt && (
                <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 4 }}>
                  Last extracted: {new Date(data.extractedAt).toLocaleString()}
                  {data.llmModel ? ` · ${data.llmModel}` : ''}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {data.active ? (
                <button
                  className="btn ghost"
                  onClick={() => setDisabled.mutate(true)}
                  disabled={setDisabled.isPending}
                >
                  Disable
                </button>
              ) : data.userDisabled ? (
                <button
                  className="btn primary"
                  onClick={() => setDisabled.mutate(false)}
                  disabled={setDisabled.isPending}
                >
                  Re-enable
                </button>
              ) : null}
              <button
                className="btn"
                onClick={() => reExtract.mutate()}
                disabled={reExtract.isPending}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title="Re-run LLM extraction from your current CV"
              >
                <RotateCw size={13} />
                {reExtract.isPending ? 'Queued…' : 'Re-extract'}
              </button>
            </div>
          </div>

          {/* CV summary (read-only) */}
          {data.cvSummary && (
            <div className="card" style={{ padding: 22, marginBottom: 14 }}>
              <div className="tab" style={{ marginBottom: 8 }}>What the LLM understood about you</div>
              <div style={{ fontSize: 13, color: 'var(--ink2)', fontStyle: 'italic', lineHeight: 1.55 }}>
                "{data.cvSummary}"
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 10 }}>
                Used as context for ambiguous-title judgement. To change it, upload a refined CV or click Re-extract.
              </div>
            </div>
          )}

          {/* Anchor chip editor */}
          <div className="card" style={{ padding: 22 }}>
            <div className="tab" style={{ marginBottom: 8 }}>Anchor keywords ({anchors.length})</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.55, marginBottom: 12 }}>
              Titles containing any of these are auto-kept (skipping the LLM judgement). Wider list = more titles short-circuit, but also more chance of letting borderline irrelevant titles through.
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {anchors.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--ink3)', fontStyle: 'italic' }}>
                  No anchors — every title goes through the LLM gate.
                </span>
              )}
              {anchors.map((a, i) => (
                <span
                  key={`${a}-${i}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    background: 'var(--line2)',
                    border: '1px solid var(--line)',
                    borderRadius: 4,
                    fontSize: 12,
                    fontFamily: 'JetBrains Mono, var(--cjk-sans), monospace',
                  }}
                >
                  {a}
                  <button
                    onClick={() => removeAnchor(i)}
                    disabled={saveAnchors.isPending}
                    style={{
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: 'var(--ink3)', padding: 0, display: 'flex', alignItems: 'center',
                    }}
                    title="Remove"
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={newAnchor}
                onChange={(e) => setNewAnchor(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addAnchor(); }}
                placeholder="Add a keyword…"
                style={{
                  flex: 1, padding: '6px 10px', border: '1px solid var(--line)',
                  borderRadius: 4, fontSize: 12.5, fontFamily: 'JetBrains Mono, var(--cjk-sans), monospace',
                  background: 'var(--bg)',
                }}
              />
              <button
                className="btn"
                onClick={addAnchor}
                disabled={saveAnchors.isPending || !newAnchor.trim()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Plus size={13} /> Add
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ink3)', lineHeight: 1.55 }}>
            Behaviour: at scrape time, titles containing an anchor are auto-kept; the rest get sent in batches to the LLM with your CV summary, which decides relevance. If the LLM is unreachable, the gate falls back to keep-all (no regression).
          </div>

          {/* Seniority signal — Phase 5 addition. The cv_extract_title_filter
              Pass-3 LLM populates these by default; this card lets the user
              correct/override when extraction guesses wrong. The values feed
              two separate gates:
                - Phase-4 deterministic hard-overshoot (jobspy-backend +
                  eval-fit-gate) — drops "Field CTO 15+ yrs req" for a
                  senior with overshoot=1
                - Cap-5 years-experience (dims-score.applyScoreCaps) —
                  caps the score for roles with X-years-required >> X-actual
              Setting any of these flips seniority_user_edited=true so the
              next CV re-extract doesn't overwrite. Click "Reset to CV value"
              below if you want re-extraction to take over again. */}
          <SeniorityCard
            data={data}
            onSave={(patch) => saveSeniority.mutate(patch)}
            saving={saveSeniority.isPending}
          />
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Seniority card — three controls bound to PUT /api/title-filter
// ────────────────────────────────────────────────────────────────────────────

const SENIORITY_LEVELS: Array<{ value: NonNullable<TitleFilterState['seniorityLevel']>; label: string; hint: string }> = [
  { value: 'junior',     label: 'Junior',     hint: '0–2 yrs' },
  { value: 'mid',        label: 'Mid',        hint: '2–4 yrs' },
  { value: 'senior',     label: 'Senior',     hint: '4–8 yrs' },
  { value: 'staff',      label: 'Staff',      hint: '8–12 yrs' },
  { value: 'principal',  label: 'Principal',  hint: '12+ yrs' },
  { value: 'director',   label: 'Director / VP / C-level', hint: 'org leader' },
];

const OVERSHOOT_CHOICES: Array<{ value: 0 | 1 | 2; label: string; hint: string }> = [
  { value: 0, label: 'Strict',  hint: 'Drop anything above your level' },
  { value: 1, label: 'Mild',    hint: 'Allow 1 level up (e.g. senior → staff)' },
  { value: 2, label: 'Open',    hint: 'Allow 2 levels up (e.g. senior → principal)' },
];

function SeniorityCard({
  data,
  onSave,
  saving,
}: {
  data: TitleFilterState;
  onSave: (patch: { seniorityLevel?: string | null; yearsExperience?: number | null; readyForOvershoot?: number | null }) => void;
  saving: boolean;
}) {
  const [level, setLevel]       = useState<string>(data.seniorityLevel ?? '');
  const [years, setYears]       = useState<string>(data.yearsExperience != null ? String(data.yearsExperience) : '');
  const [overshoot, setOvershoot] = useState<string>(data.readyForOvershoot != null ? String(data.readyForOvershoot) : '');

  // Re-sync when the underlying row changes (Re-extract just landed,
  // or another tab saved). Skip when the user has unsaved edits in
  // any of the three inputs that differ from server state.
  useEffect(() => {
    setLevel(data.seniorityLevel ?? '');
    setYears(data.yearsExperience != null ? String(data.yearsExperience) : '');
    setOvershoot(data.readyForOvershoot != null ? String(data.readyForOvershoot) : '');
  }, [data.seniorityLevel, data.yearsExperience, data.readyForOvershoot]);

  const dirty =
    (level || '') !== (data.seniorityLevel ?? '') ||
    (years || '') !== (data.yearsExperience != null ? String(data.yearsExperience) : '') ||
    (overshoot || '') !== (data.readyForOvershoot != null ? String(data.readyForOvershoot) : '');

  const handleSave = () => {
    const yearsNum = years.trim() === '' ? null : Number(years);
    if (yearsNum != null && (!Number.isFinite(yearsNum) || yearsNum < 0 || yearsNum > 60)) {
      alert('Years of experience must be a number between 0 and 60.');
      return;
    }
    const overshootNum = overshoot === '' ? null : Number(overshoot);
    onSave({
      seniorityLevel:    level === '' ? null : level,
      yearsExperience:   yearsNum,
      readyForOvershoot: overshootNum,
    });
  };

  return (
    <div className="card" style={{ padding: 22, marginTop: 14 }}>
      <div className="tab" style={{ marginBottom: 8 }}>Seniority signal</div>
      <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.55, marginBottom: 14 }}>
        Drives the deterministic seniority gate at scrape AND evaluate time.
        Pre-populated from your CV; correct it if the auto-extraction missed.
        {data.seniorityUserEdited && (
          <span style={{ marginLeft: 6, color: 'var(--ink2)' }}>
            · You've edited this — CV re-extracts will leave it alone until you reset.
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr', gap: 16, marginBottom: 14 }}>
        <div>
          <div className="tab" style={{ marginBottom: 4, fontSize: 10.5 }}>Level</div>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            style={{
              width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
              borderRadius: 4, fontSize: 13, background: 'var(--bg)',
            }}
          >
            <option value="">— pick one —</option>
            {SENIORITY_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>{l.label} ({l.hint})</option>
            ))}
          </select>
        </div>
        <div>
          <div className="tab" style={{ marginBottom: 4, fontSize: 10.5 }}>Years of experience</div>
          <input
            type="number"
            min={0}
            max={60}
            value={years}
            onChange={(e) => setYears(e.target.value)}
            placeholder="3"
            style={{
              width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
              borderRadius: 4, fontSize: 13, background: 'var(--bg)',
            }}
          />
        </div>
        <div>
          <div className="tab" style={{ marginBottom: 4, fontSize: 10.5 }}>Ready to stretch?</div>
          <select
            value={overshoot}
            onChange={(e) => setOvershoot(e.target.value)}
            style={{
              width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
              borderRadius: 4, fontSize: 13, background: 'var(--bg)',
            }}
          >
            <option value="">— pick one —</option>
            {OVERSHOOT_CHOICES.map((o) => (
              <option key={o.value} value={String(o.value)}>{o.label} — {o.hint}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="btn primary"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : dirty ? 'Save seniority' : 'Saved'}
        </button>
        {data.seniorityUserEdited && (
          <button
            className="btn ghost"
            onClick={() => onSave({
              seniorityLevel: null,
              yearsExperience: null,
              readyForOvershoot: null,
              // resetUserEdited:true releases the pin so the next CV
              // re-extract repopulates these from the LLM.
              ...({ resetUserEdited: true } as object),
            })}
            disabled={saving}
            title="Clear your manual values + release the pin so the next CV re-extract takes over."
            style={{ fontSize: 12 }}
          >
            Reset to CV value
          </button>
        )}
        {data.seniorityExtractedAt && !data.seniorityUserEdited && (
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
            From CV at {new Date(data.seniorityExtractedAt).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}
